import { SelectOptions } from "components/Select/Select.d";
import {
  cpnFormatter,
  hnoFormatter,
  translateToLocalDate,
} from "utils/helpers";

export type AliRecord = {
  msag_id?: number | undefined;
  orig_msag_id?: number | undefined;
  sop_id?: number | undefined;
  FOC?: string | undefined;
  CPN?: string | undefined;
  HNP?: string | undefined;
  HNO?: string | undefined;
  HNE?: string | undefined;
  HNS?: string | undefined;
  PRD?: string | undefined;
  STN?: string | undefined;
  STS?: string | undefined;
  POD?: string | undefined;
  MCN?: string | undefined;
  STA?: string | undefined;
  LOC?: string | undefined;
  NAM?: string | undefined;
  CLS?: string | undefined;
  TYS?: string | undefined;
  Exchange?: string | undefined;
  lecESN?: string | undefined;
  MainNPA?: string | undefined;
  MainNumber?: string | undefined;
  OrderNumber?: string | undefined;
  ExtractDate?: string | undefined;
  lecCOI?: string | undefined;
  CPF?: string | undefined;
  SourceID?: string | undefined;
  lecZipCode?: string | undefined;
  lecZipExtension?: string | undefined;
  GeneralUse?: string | undefined;
  CustomerCode?: string | undefined;
  Comments?: string | undefined;
  lecLON?: string | undefined;
  lecLAT?: string | undefined;
  lecELV?: string | undefined;
  CellID?: string | undefined;
  SectorID?: string | undefined;
  TARCode?: string | undefined;
  Reserved1?: string | undefined;
  AltNumber?: string | undefined;
  ExpandedExtractDate?: string | undefined;
  NENAReserved?: string | undefined;
  DataProviderID?: string | undefined;
  Reserved2?: string | undefined;
  privateCit?: boolean;
  dd?: number;
  esuri?: string | undefined;
  inESN?: string | undefined;
  ALIsteering?: string | undefined;
  FDAY?: string | undefined;
  GDAY?: string | undefined;
  Community?: string | undefined;
  COI?: string | undefined;
  ZIP?: string | undefined;
  LON?: string | undefined;
  LAT?: string | undefined;
  ELV?: string | undefined;
  FIR?: string | undefined;
  EMS?: string | undefined;
  LAW?: string | undefined;
  RA1?: string | undefined;
  RA2?: string | undefined;
  RA3?: string | undefined;
  dCode?: string | undefined;
  created?: string | undefined;
  created_by?: string | undefined;
  last_modified?: string | undefined;
  modified_by?: string | undefined;
  web_login?: string | undefined;
  ali_tmp_idx?: number | undefined;
  job_id?: number | undefined;
  NOTES?: string | undefined;
  occurred?: string | undefined;
  submitted?: string | undefined;
};

export interface ALIRecordRequest {
  CPN: string;
  FOC: string;
  NAM: string;
  STA: string;
  MCN: string;
  STN: string;
  STS: string;
  PRD: string;
  POD: string;
  HNO: string;
  HNS: string;
  LOC: string;
  CPF: string;
  CLS: string;
  TYS: string;
  ZIP: string;
  Comments: string;
  lecZipCode: string;
  lecZipExtension: string;
  lecESN: string;
  MainNPA: string;
  MainNumber: string;
  Exchange: string;
  OrderNumber: string;
  lecLAT: string;
  lecLON: string;
  lecELV: string;
  NENAReserved: string;
  DataProviderID: string;
  Reserved2: string;
  GeneralUse: string;
  NOTES: string;
}

interface ADDLRequest {
  privateCit: boolean;
  dd: number | undefined;
}

export function getDefaultALIRecordRequest(): ALIRecordRequest {
  return {
    FOC: "I",
    CPN: "",
    HNP: "",
    HNO: "",
    HNE: "",
    HNS: "",
    PRD: "",
    STN: "",
    STS: "",
    POD: "",
    MCN: "",
    STA: "",
    LOC: "",
    NAM: "",
    CLS: classOfServiceOptions[0].value,
    TYS: typeOfServiceOptions[0].value,
    Exchange: "",
    lecESN: "",
    MainNPA: "",
    MainNumber: "",
    OrderNumber: "",
    ExtractDate: "",
    Comments: "",
    lecCOI: "",
    CPF: "",
    SourceID: "",
    lecZipCode: "",
    lecZipExtension: "",
    GeneralUse: "",
    lecLON: "",
    lecLAT: "",
    lecELV: "",
    NENAReserved: "",
    DataProviderID: "",
    Reserved2: "",
  };
}

/**
 * Editable Record Request Fields:
 * - CPN: Customer Phone Number (formatted)
 * - NAM: Customer Name
 * - STA: State (autocomplete from MSAG)
 * - MCN: MSAG Community Name (autocomplete from MSAG)
 * - STN: Street Name (autocomplete from MSAG)
 * - STS: Street Suffix (autocomplete from MSAG)
 * - PRD: Prefix Directional (auto-populated from MSAG)
 * - POD: Post Directional (auto-populated from MSAG)
 * - HNO: House Number
 * - HNS: House Number Suffix
 * - LOC: Location
 * - CPF: NENA ID (dropdown from user options)
 * - CLS: Class of Service (dropdown)
 * - TYS: Type of Service (dropdown)
 * - Comments: Comments
 * - lecZipCode: Zip Code
 * - lecZipExtension: Zip Extension
 * - MainNPA: Area Code of Main Number (auto-populated from CPN)
 * - MainNumber: Main Number (auto-populated from CPN)
 * - Exchange: Exchange (auto-populated from MSAG)
 * - OrderNumber: Order Number
 * - lecLAT: Latitude
 * - lecLON: Longitude
 * - lecELV: Elevation
 * - NENAReserved: NENA Reserved
 * - DataProviderID: Data Provider ID
 * - Reserved2: Reserved 2
 *
 * Auto-populated fields (not directly editable):
 * - lecCOI: County ID (from MSAG)
 * - GeneralUse: General Use (from MSAG)
 *
 */

export const remove = (cell, formatterParams, onRendered) => {
  return `<calcite-icon id="trashIcon" icon="trash" scale="s"></calcite-icon>`;
};
export const edit = (cell, formatterParams, onRendered) => {
  return `<calcite-icon id="pencilIcon" icon="pencil" scale="s"></calcite-icon>`;
};

export const options = (cell, formatterParams, onRendered) => {
  let id = cell.getData().CPN;
  const hasWritePermission = formatterParams?.hasWritePermission;
  const hasChangeRequestPermission =
    formatterParams?.hasChangeRequestPermission;
  const hasAliLogViewPermission = formatterParams?.hasAliLogViewPermission;

  let opts = "";

  if (hasWritePermission) {
    opts += `
      <a href="/ali-records/edit/${id}" title="Edit ALI Record">
        <calcite-icon id="editRecord-${id}" icon="pencil" scale="s" style="margin-right:8px;"></calcite-icon>
      </a>
      <a href="/ali-records/delete/${id}" title="Delete ALI Record">
        <calcite-icon id="deleteRecord-${id}" icon="trash" scale="s"></calcite-icon>
      </a>
    `;
  } else if (hasChangeRequestPermission) {
    opts += `
      <a href="/ali-records/edit/${id}" title="Request Change for ALI Record">
        <calcite-icon id="editRecord-${id}" icon="pencil" scale="s"></calcite-icon>
      </a>
    `;
  } else {
    // read only
    opts += `
      <a href="/ali-records/view/${id}" title="View ALI Record">
        <calcite-icon id="viewRecord-${id}" icon="view-visible" scale="s"></calcite-icon>
      </a>
    `;
  }

  if (hasAliLogViewPermission) {
    opts += `<a href="/ali-logs?cpn=${id}" title="View ALI Logs"><calcite-icon icon="clock-forward" scale="s" style="margin-left:8px;"></calcite-icon></a>`;
  }

  return opts;
};

export function getAliExportColumns() {
  return {
    "Telephone Number": "CPN",
    "House Number": "HNO",
    "House Number Suffix": "HNS",
    "Prefix Directional": "PRD",
    "Street Name": "STN",
    "Street Suffix": "STS",
    "Post Directional": "POD",
    "MSAG Community Name": "MCN",
    County: "lecCOI",
    State: "STA",
    "ZIP Code": "ZIP",
    Location: "LOC",
    "Customer Name": "NAM",
    "NENA ID": "CPF",
    "General Use": "GeneralUse",
    "NENA Reserved": "NENAReserved",
    "Data Provider ID": "DataProviderID",
    "Emergency Service Number": "lecESN",
    "Class of Service": "CLS",
    "Type of Service": "TYS",
    Comments: "Comments",
    "Last Modified": "last_modified",
  };
}

export function getAliColumns(permissions: Array) {
  let aliColumns: ColumnDefinition[] = [
    // Displayed columns
    {
      title: "",
      field: "actions",
      formatter: options,
      formatterParams: {
        hasWritePermission: permissions.includes("foresite:ali:write"),
        hasChangeRequestPermission: permissions.includes(
          "foresite:ali:requestChange",
        ),
        hasAliLogViewPermission: permissions.includes("foresite:ali_log:view"),
      },
      width: 75,
      minWidth: 50,
      hozAlign: "center",
      headerSort: false,
      headerFilter: false,
    },
    {
      title: "Telephone Number",
      field: "CPN",
      formatter: cpnFormatter,
      headerFilter: "input",
      headerFilterLiveFilter: false,
      width: 185,
      // minWidth: 120,
    },
    {
      title: "House Number",
      field: "HNO",
      formatter: hnoFormatter,
      headerFilter: "input",
      headerFilterLiveFilter: false,
      width: 155,
      // minWidth: 50,
    },
    {
      title: "House Number Suffix",
      field: "HNS",
      headerFilter: "input",
      headerFilterLiveFilter: false,
      width: 200,
      // minWidth: 50,
    },
    {
      title: "Prefix Directional",
      field: "PRD",
      headerFilter: "input",
      headerFilterLiveFilter: false,
      width: 170,
      // minWidth: 50,
    },
    {
      title: "Street Name",
      field: "STN",
      headerFilter: "input",
      headerFilterLiveFilter: false,
      width: 135,
      // minWidth: 60,
    },
    {
      title: "Street Suffix",
      field: "STS",
      headerFilter: "input",
      headerFilterLiveFilter: false,
      width: 135,
      // minWidth: 50,
    },
    {
      title: "Post Directional",
      field: "POD",
      headerFilter: "input",
      headerFilterLiveFilter: false,
      width: 155,
      // minWidth: 50,
    },
    {
      title: "MSAG Community Name",
      field: "MCN",
      headerFilter: "input",
      headerFilterLiveFilter: false,
      width: 230,
      // minWidth: 110,
    },
    {
      title: "County",
      field: "lecCOI",
      headerFilter: "input",
      headerFilterLiveFilter: false,
      width: 100,
      // minWidth: 50,
    },
    {
      title: "State",
      field: "STA",
      headerFilter: "input",
      headerFilterLiveFilter: false,
      width: 100,
      // minWidth: 50,
    },
    {
      title: "Location",
      field: "LOC",
      headerFilter: "input",
      headerFilterLiveFilter: false,
      width: 110,
      // minWidth: 110,
    },
    {
      title: "Customer Name",
      field: "NAM",
      headerFilter: "input",
      headerFilterLiveFilter: false,
      width: 175,
      // minWidth: 110,
    },
    permissions.includes("foresite:ali:addlData:write") && {
      title: "Private Citizen",
      field: "privateCit",
      formatter: (cell) => {
        const val = cell.getValue();
        return val
          ? `<calcite-icon icon="check" scale="m" style="color: green;"></calcite-icon>`
          : `<calcite-icon icon="x" scale="m" style="color: red;"></calcite-icon>`;
      },
      headerFilter: "select",
      headerFilterParams: {
        values: [
          { label: "", value: "" },
          { label: "True", value: "1" },
          { label: "False", value: "0" },
        ],
        sort: "asc",
      },
      headerFilterLiveFilter: true,
      width: 150,
      hozAlign: "center",
      // minWidth: 50,
    },
    permissions.includes("foresite:ali:addlData:write") && {
      title: "Disability Codes",
      field: "dCode",
      headerFilter: "input",
      headerFilterLiveFilter: false,
      width: 165,
      // minWidth: 50,
    },
    {
      title: "NENA ID",
      field: "CPF",
      headerFilter: "input",
      headerFilterLiveFilter: false,
      width: 120,
      // minWidth: 50,
    },
    {
      title: "General Use",
      field: "GeneralUse",
      headerFilter: "input",
      headerFilterLiveFilter: false,
      width: 120,
      // minWidth: 50,
    },
    {
      title: "ZIP Code",
      field: "lecZipCode",
      headerFilter: "input",
      headerFilterLiveFilter: false,
      width: 120,
      // minWidth: 50,
    },
    {
      title: "ZIP Extension",
      field: "lecZipExtension",
      headerFilter: "input",
      headerFilterLiveFilter: false,
      width: 120,
      // minWidth: 50,
    },
    {
      title: "ESN",
      field: "lecESN",
      headerFilter: "input",
      headerFilterLiveFilter: false,
      width: 100,
      // minWidth: 50,
    },
    {
      title: "Type of Service",
      field: "TYS",
      headerFilter: "input",
      headerFilterLiveFilter: false,
      width: 155,
      // minWidth: 50,
    },
    {
      title: "Class of Service",
      field: "CLS",
      headerFilter: "input",
      headerFilterLiveFilter: false,
      width: 155,
      // minWidth: 50,
    },
    {
      title: "Data Provider ID",
      field: "DataProviderID",
      headerFilter: "input",
      headerFilterLiveFilter: false,
      width: 155,
      // minWidth: 50,
    },
    {
      title: "FOC",
      field: "FOC",
      headerFilter: "input",
      headerFilterLiveFilter: false,
      width: 100,
      // minWidth: 50,
    },
    {
      title: "Extract Date",
      field: "ExtractDate",
      // headerFilter: "input",
      // headerFilterLiveFilter: false,
      width: 175,
      // minWidth: 110,
    },
    {
      title: "Last Modified",
      field: "last_modified",
      mutator: translateToLocalDate,
      headerFilter: "input",
      headerFilterLiveFilter: false,
      width: 175,
    },
  ].filter(Boolean); // Remove any undefined values

  // Only add the empty column once at the start if hasEditPermission is true
  // if(hasEditPermission) {
  //   aliColumns.unshift({
  //     title: "",
  //     field: "actions",
  //     formatter: options,
  //     width: 75,
  //     minWidth: 50,
  //     hozAlign: "center",
  //     headerSort: false,
  //     headerFilter: false,
  //   });
  // }

  return aliColumns;
}

export function getBulkChangeRequestColumns(permissions: Array) {
  let aliColumns: ColumnDefinition[] = [
    {
      title: "Telephone Number",
      field: "CPN",
      formatter: cpnFormatter,
    },
    {
      title: "House Number",
      field: "HNO",
      formatter: hnoFormatter,
    },
    {
      title: "House Number Suffix",
      field: "HNS",
    },
    {
      title: "Prefix Directional",
      field: "PRD",
    },
    {
      title: "Street Name",
      field: "STN",
    },
    {
      title: "Street Suffix",
      field: "STS",
    },
    {
      title: "Post Directional",
      field: "POD",
    },
    {
      title: "MSAG Community Name",
      field: "MCN",
    },
    {
      title: "County",
      field: "lecCOI",
    },
    {
      title: "State",
      field: "STA",
    },
    {
      title: "Location",
      field: "LOC",
    },
  ];

  return aliColumns;
}

//only I, C, D, U, M, E allowed)
export const functionOfChangeOptions: SelectOptions[] = [
  { value: "I", label: "I-Insert" },
  { value: "M", label: "M-Migrate" },
  { value: "D", label: "D-Delete" },
  { value: "C", label: "C-Change" },
  { value: "U", label: "U-Unlock" },
];

export const functionOfChangeEditOptions: SelectOptions[] = [
  functionOfChangeOptions[3],
  functionOfChangeOptions[4],
];

export const functionOfChangeCreateOptions: SelectOptions[] = [
  functionOfChangeOptions[0],
];

export const functionOfChangeMigrateOptions: SelectOptions[] = [
  functionOfChangeOptions[1],
];

export const classOfServiceOptions: SelectOptions[] = [
  { value: "1", label: "1-Residence" },
  { value: "2", label: "2-Business" },
  { value: "3", label: "3-Residence PBX" },
  { value: "4", label: "4-Business PBX" },
  { value: "5", label: "5-Centrex" },
  { value: "6", label: "6-Coin 1 Way out" },
  { value: "7", label: "7-Coin 2 Way" },
  { value: "8", label: "8-Mobile" },
  { value: "9", label: "9-Residence OPX" },
  { value: "0", label: "0-Business OPX" },
  { value: "A", label: "A-Customer Owned Coin Telephone(COCT)" },
  { value: "B", label: "B-Not Available" },
  { value: "C", label: "C-VoIP Residence" },
  { value: "D", label: "D-VoIP Business" },
  { value: "E", label: "E-VoIP Coin/Pay Phone" },
  { value: "F", label: "F-VoIP Wireless" },
  { value: "J", label: "J-VoIP Nomadic" },
  { value: "K", label: "K-VoIP Enterprise Services - Centrex & PBX" },
  { value: "V", label: "V-VoIP Services Default COS" },
];

export const typeOfServiceOptions: SelectOptions[] = [
  { value: "0", label: "0-Not FX nor Non-Published" },
  { value: "1", label: "1-FX in 911 serving area" },
  { value: "2", label: "2-FX outside 911 serving area" },
  { value: "3", label: "3-Non-Published" },
  { value: "4", label: "4-Non-Published FX in serving area" },
  { value: "5", label: "5-Non-Published FX outside 911 serving area" },
  { value: "6", label: "6-Local Ported Number (LNP)" },
  { value: "7", label: "7-Interim Ported Number" },
];

export const disabilityOptions: SelectOptions[] = [
  { value: 1, label: "Life Support System" },
  { value: 2, label: "Mobility Impaired" },
  { value: 4, label: "Blind" },
  { value: 8, label: "Deaf & Hard of Hearing" },
  { value: 16, label: "Speech Impaired" },
  { value: 32, label: "Developmentally Disabled" },
  { value: 64, label: "Teletypewriter Device" },
];

// Example usage of disabilityOptions to create a bitmask
export function calculateDisabilityBitmask(
  selectedOptions: (number | string)[],
): number {
  return selectedOptions.reduce((acc, curr) => acc | Number(curr), 0);
}

// Example usage of disabilityOptions to decode a bitmask
export function decodeDisabilityBitmask(bitmask: number | string): number[] {
  const numericBitmask = Number(bitmask);
  return disabilityOptions
    .filter((option) => (numericBitmask & option.value) === option.value)
    .map((option) => option.value);
}
